import type * as React from 'react'
import { enUS, fr } from 'date-fns/locale'
import type { Locale } from 'date-fns'

import { cn } from '@repo/ui/lib/utils'

export const DEFAULT_YEAR_START = 1900
export const DEFAULT_YEAR_END = 2100

export const MONTH_INDICES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]

export const buildYearRange = (yearStart: number, yearEnd: number) =>
  Array.from({ length: yearEnd - yearStart + 1 }, (_, i) => yearStart + i)

export const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate()

type IntlWeekInfo = { firstDay: number }

// Fallback for engines without Intl weekInfo (Firefox ships neither shape): the same
// date-fns locales Calendar.tsx maps the app's languages to, whose weekStartsOn is
// engine-independent.
const DATE_FNS_LOCALES: Record<string, Locale> = { en: enUS, fr }

/** The locale's first day of the week as a JS `getDay()` index (0 = Sunday). */
export const firstWeekdayForLocale = (locale: string): number => {
  try {
    const resolved = new Intl.Locale(locale)
    // V8 exposes weekInfo as an accessor; the final spec shape is a getWeekInfo() method.
    const weekInfo =
      (resolved as { weekInfo?: IntlWeekInfo }).weekInfo ??
      (resolved as { getWeekInfo?: () => IntlWeekInfo }).getWeekInfo?.()
    // Intl numbers days 1 (Monday) through 7 (Sunday).
    if (weekInfo) return weekInfo.firstDay % 7
  } catch {
    // An invalid tag: keep going, the language-subtag fallback may still match.
  }
  const language = locale.toLowerCase().split('-')[0] ?? ''
  return DATE_FNS_LOCALES[language]?.options?.weekStartsOn ?? 0
}

/** Clamps `date` into [min, max]; the bounds must already be normalized to the picker's granularity. */
export const clampDate = (date: Date, min: Date, max: Date) =>
  date.getTime() < min.getTime() ? min : date.getTime() > max.getTime() ? max : date

export const stepperIconClass =
  'text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50'

// Deliberately no color transition: cells change state from re-renders too (month navigation
// re-using a cell's DOM node, a click's selection landing while the popover close-animates),
// and those must swap instantly rather than flash an animation.
export const gridCellClass = (isSelected: boolean) =>
  cn(
    'disabled:text-muted-foreground/40 rounded text-center text-sm font-medium disabled:pointer-events-none',
    isSelected
      ? 'bg-primary text-primary-foreground'
      : 'hover:bg-accent hover:text-accent-foreground',
  )

/**
 * Arrow-key navigation for a grid of cell buttons: left/right move one cell,
 * up/down move one row. Attach to the grid container. A move stops at a disabled
 * cell or the grid's edge. Cells use a roving tabindex, so the focused cell may
 * carry tabindex -1 — that's fine, focus() doesn't care.
 */
export const gridKeyDownHandler =
  (columns: number) => (event: React.KeyboardEvent<HTMLElement>) => {
    const deltas: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -columns,
      ArrowDown: columns,
    }
    const delta = deltas[event.key]
    if (delta === undefined) return
    const cells = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button'))
    const from = cells.indexOf(event.target as HTMLButtonElement)
    if (from === -1) return
    const next = cells[from + delta]
    if (!next || next.disabled) return
    event.preventDefault()
    next.focus()
  }
